import { useCallback, useReducer } from 'react'
import moment from 'moment'
import { debounce } from 'lodash'
import { useIntl } from 'react-intl'
import effects from 'app/effects/effects'
import { useDispatch } from 'react-redux'
import momentTimezone from 'moment-timezone'
import { mergeAll, mergeLeft, isEmpty } from 'ramda'

import { usePlace } from 'commons/usePlace'
import { dateGeneralFormat } from 'commons/date'
import MessageAlert from 'app/components/MessageAlert'
import { networkErrorDetails, networkErrorToMessage } from 'commons/error'

import {
  filterType,
  aiLogFilterStatus,
  AI_LOG_DISCARDED,
  AI_LOG_CREATED,
} from 'app/main/plansAndBilling/constants'

export const ActionType = {
  SET_AI_LOG_DETAIL: 'SET_AI_LOG_DETAIL',
  SET_AI_USAGE_LOG: 'SET_AI_USAGE_LOG',
  SET_LOADER_AI_LOG_DETAIL: 'SET_LOADER_AI_LOG_DETAIL',
  SET_SELECTED_DATE: 'SET_SELECTED_DATE',
  SET_EMPTY_AI_USAGE_LOG: 'SET_EMPTY_AI_USAGE_LOG',
  SET_LOADER_AI_USAGE_LOG: 'SET_LOADER_AI_USAGE_LOG',
}

const initialState = {
  params: {
    page: 1,
    limit: 10,
    startDate: moment().subtract(30, 'days').format(dateGeneralFormat),
    endDate: moment().endOf('day').format(dateGeneralFormat),
    timezone: momentTimezone.tz.guess(true),
    status: `${AI_LOG_DISCARDED}, ${AI_LOG_CREATED}`,
  },
  filterStatus: aiLogFilterStatus,
  dateRang: null,
  isEmptyAiUsageLog: false,
  isLoading: true,
  filter: filterType.LAST_THIRTY_DAYS,
  aiCreditDetail: {},
  aiUsageLog: { data: [], meta: {} },
  isLoadingLogDetail: false,
}

const reducer = (state, action) => {
  switch (action.type) {
    case ActionType.SET_LOADER_AI_USAGE_LOG:
      return {
        ...state,
        isLoading: action.value,
      }
    case ActionType.SET_EMPTY_AI_USAGE_LOG:
      return {
        ...state,
        isEmptyAiUsageLog: true,
        isLoading: false,
      }
    case ActionType.SET_AI_USAGE_LOG:
      return {
        ...state,
        aiUsageLog: action.value,
        params: action.params,
        isLoading: false,
      }
    case ActionType.SET_SELECTED_DATE:
      return {
        ...state,
        filter: action.filter,
        dateRang: action.dateRang,
      }
    case ActionType.SET_LOADER_AI_LOG_DETAIL:
      return {
        ...state,
        isLoadingLogDetail: action.value,
        aiCreditDetail: {},
      }
    case ActionType.SET_AI_LOG_DETAIL:
      return {
        ...state,
        aiCreditDetail: action.value,
        isLoadingLogDetail: false,
      }
    default:
      return state
  }
}

export default function useAiUsageLog() {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { notification } = usePlace()
  const { formatMessage } = useIntl()
  const commonDispatch = useDispatch()

  const handleAlertError = useCallback(
    (error) => {
      const { message } = networkErrorToMessage(formatMessage)(error)
      const errorDetails = networkErrorDetails(error)

      return commonDispatch(
        MessageAlert.showError({
          message: message ?? errorDetails.message,
        }),
      )
    },
    [commonDispatch, formatMessage],
  )

  const fetchTeamMembers = useCallback(
    (params = initialState.params, isFirstTime = false) => {
      effects.apis
        .getAiUsageLogt(params)
        .then((response) => {
          if (
            isFirstTime &&
            response.meta.total === 0 &&
            notification.total === 0
          )
            dispatch({ type: 'SET_EMPTY_AI_USAGE_LOG' })
          else
            dispatch({
              type: 'SET_AI_USAGE_LOG',
              value: response,
              params: params,
            })
        })
        .catch((error) => handleAlertError(error))
    },
    [handleAlertError],
  )

  const handleFetchAiUsageLog = useCallback(
    async (params, isFirstTime = false) => {
      dispatch({ type: 'SET_LOADER_AI_USAGE_LOG', value: true })
      await fetchTeamMembers(params, isFirstTime)
    },
    [fetchTeamMembers],
  )

  const debounceFetchAiUsageLog = useCallback(
    debounce((value) => {
      handleFetchAiUsageLog(value)
    }, 1000),
    [],
  )

  const handleStartEndDate = (key) => {
    switch (key) {
      case filterType.ALL:
        return { startDate: '', endDate: '' }
      case filterType.TODAY:
        return {
          startDate: moment().startOf('day').format(dateGeneralFormat),
          endDate: moment().endOf('day').format(dateGeneralFormat),
        }
      case filterType.LAST_SEVEN_DAYS:
        return {
          startDate: moment().subtract(7, 'days').format(dateGeneralFormat),
          endDate: moment().endOf('day').format(dateGeneralFormat),
        }
      case filterType.LAST_THIRTY_DAYS:
        return {
          startDate: moment().subtract(30, 'days').format(dateGeneralFormat),
          endDate: moment().endOf('day').format(dateGeneralFormat),
        }
      case filterType.LAST_SIX_MONTHS:
        return {
          startDate: moment().subtract(6, 'months').format(dateGeneralFormat),
          endDate: moment().endOf('day').format(dateGeneralFormat),
        }
      case filterType.LAST_YEAR:
        return {
          startDate: moment().subtract(1, 'years').format(dateGeneralFormat),
          endDate: moment().endOf('day').format(dateGeneralFormat),
        }
      default:
        return { startDate: '', endDate: '' }
    }
  }

  const setFilter = (filter, dateRang = null) => {
    dispatch({
      type: 'SET_SELECTED_DATE',
      filter: filter,
      dateRang: dateRang,
    })
  }

  const handleFilter = (value) => {
    setFilter(value, null)
    handleFetchAiUsageLog(
      mergeLeft(handleStartEndDate(value), initialState.params),
    )
  }

  const handleDateFilter = ({ startDate = '', endDate = '' }) => {
    setFilter(null, { startDate, endDate, key: 'selection' })
    const startEndDate = {
      startDate: moment(startDate).format('YYYY-MM-DD'),
      endDate: moment(endDate).format('YYYY-MM-DD'),
    }

    const setParams = mergeAll([
      { ...state.params, ...startEndDate, page: 1, limit: 10 },
    ])
    handleFetchAiUsageLog(setParams)
  }

  const handleFilterStatus = (typeSelected = []) => {
    const isSelected = typeSelected.filter((type) => type.checked)
    let selectedId = []
    if (isEmpty(isSelected))
      selectedId = typeSelected.map((selected) => selected.id)
    else selectedId = isSelected.map((selected) => selected.id)

    const setParams = mergeAll([
      { ...state.params, status: selectedId.toString(), page: 1, limit: 10 },
    ])
    debounceFetchAiUsageLog(setParams)
  }

  const fetchAiLogDetail = useCallback(
    (requestId) => {
      effects.apis
        .getAiUsageDetail(requestId)
        .then((response) => {
          dispatch({ type: 'SET_AI_LOG_DETAIL', value: response })
        })
        .catch((error) => {
          handleAlertError(error)
        })
    },
    [handleAlertError],
  )

  const handleFetchAiLogDetail = useCallback(
    async (requestId) => {
      dispatch({ type: 'SET_LOADER_AI_LOG_DETAIL', value: true })
      await fetchAiLogDetail(requestId)
    },
    [fetchAiLogDetail],
  )

  return {
    state,
    handleFilter,
    handleDateFilter,
    handleFilterStatus,
    handleFetchAiUsageLog,
    handleFetchAiLogDetail,
  }
}
